/**
 * Toast.tsx — 轻量 toast 提示系统
 *
 * 移植自原型 design/Novel-admin/src/toast.jsx，但按 REBUILD-CONTRACT §2.3 改造：
 * - 不再用原型那种独立 ReactDOM.createRoot 自挂 DOM；改为导出 React 组件 ToastHost()，
 *   在 App 里挂一次，内部用 useState 维护队列。
 * - 导出命令式 toast(text, opts) 函数：通过 module 级 push 引用把消息推进当前挂载的 ToastHost。
 * tone 配色、动画、布局 1:1 照原型保真。
 */

import { type CSSProperties, type ReactElement, createElement, useEffect, useState } from 'react';
import { Icons } from '~/lib/icons';

/** 单条 toast 的色调 */
type ToastTone = 'default' | 'pos' | 'warn' | 'neg' | 'info';

/** toast() 的可选参数 */
interface ToastOptions {
  tone?: ToastTone;
  icon?: keyof typeof Icons;
  duration?: number;
}

/** 队列中的一条 toast */
interface ToastItem {
  id: string;
  text: string;
  tone: ToastTone;
  icon?: keyof typeof Icons;
  duration: number;
}

// module 级 push 引用：ToastHost 挂载后填入；toast() 调用时经此推入队列
let _push: ((t: ToastItem) => void) | null = null;

/** 成功 / 中性提示只需确认「做了」，一眼即可 */
const DEFAULT_DURATION_MS = 2400;
/** 失败提示要读懂原因再照做（校验失败会带字段级说明），给足阅读时间 */
const NEGATIVE_DURATION_MS = 6000;

/** 命令式弹出一条 toast；ToastHost 未挂载时静默丢弃（与原型一致） */
export function toast(text: string, opts?: ToastOptions): void {
  const o = opts || {};
  if (!_push) return;
  const tone = o.tone || 'default';
  _push({
    id: Math.random().toString(36).slice(2, 9),
    text,
    tone,
    icon: o.icon,
    duration: o.duration || (tone === 'neg' ? NEGATIVE_DURATION_MS : DEFAULT_DURATION_MS),
  });
}

/**
 * 同屏最多留几条。
 *
 * 队列此前无上限也不去重：失败驻留 6s 后，同一条校验失败连点保存会在窗口内叠成一列，
 * 盖住运营正要照提示修改的表单。去重 + 上限把「提示」限制在提示的本分内。
 */
const MAX_VISIBLE_TOASTS = 3;

export function ToastHost(): ReactElement {
  const [items, setItems] = useState<ToastItem[]>([]);
  useEffect(() => {
    _push = (t) => {
      setItems((prev) => {
        // 同 tone 同文案已在屏上 → 不叠新的（重复三遍不会让人更懂，只会挡住界面），
        // 但要换成新条目让驻留窗口重新计时：否则重试恰好落在窗口末尾时（首次失败 t=0、
        // 重试失败 t≈5.5s），运营只看到提示 0.5s 后消失，等于这次重试没有反馈。
        const deduped = prev.filter((x) => !(x.text === t.text && x.tone === t.tone));
        const next = [...deduped, t];
        return next.length > MAX_VISIBLE_TOASTS
          ? next.slice(next.length - MAX_VISIBLE_TOASTS)
          : next;
      });
      setTimeout(() => setItems((prev) => prev.filter((x) => x.id !== t.id)), t.duration);
    };
    return () => {
      _push = null;
    };
  }, []);
  return (
    <div
      style={{
        position: 'fixed',
        bottom: 20,
        right: 20,
        zIndex: 9999,
        display: 'flex',
        flexDirection: 'column-reverse',
        gap: 8,
        pointerEvents: 'none',
      }}
    >
      {items.map((t) => {
        const toneMap: Record<ToastTone, { bg: string; color: string }> = {
          default: { bg: 'var(--fg-strong)', color: 'var(--bg)' },
          pos: { bg: 'var(--pos)', color: 'white' },
          warn: { bg: 'var(--warn)', color: 'white' },
          neg: { bg: 'var(--neg)', color: 'white' },
          info: { bg: 'var(--info)', color: 'white' },
        };
        const tone = toneMap[t.tone] || {};
        const itemStyle: CSSProperties = {
          background: tone.bg,
          color: tone.color,
          padding: '10px 14px',
          borderRadius: 'var(--radius-md)',
          fontSize: 12.5,
          boxShadow: 'var(--shadow-lg)',
          display: 'flex',
          alignItems: 'center',
          gap: 10,
          minWidth: 220,
          maxWidth: 360,
          pointerEvents: 'auto',
          animation: 'fade-in 0.18s ease',
          // 校验失败文案可能回显用户输入（无空白的长串）；不强制断行会横向撑破容器
          overflowWrap: 'anywhere',
        };
        return (
          <div key={t.id} style={itemStyle}>
            {t.icon ? (
              createElement(Icons[t.icon], { size: 14 })
            ) : t.tone === 'pos' ? (
              <Icons.IconCheckCircle size={14} />
            ) : t.tone === 'warn' ? (
              <Icons.IconAlert size={14} />
            ) : t.tone === 'neg' ? (
              <Icons.IconAlert size={14} />
            ) : (
              <Icons.IconInfo size={14} />
            )}
            <span style={{ flex: 1, minWidth: 0 }}>{t.text}</span>
          </div>
        );
      })}
    </div>
  );
}
